import { ReactHTMLElement, useState } from "react";
import { AiOutlineUser } from "react-icons/ai";
import { IoBusinessOutline } from "react-icons/io5";
import { PiWarningCircleBold } from "react-icons/pi";
import { Paths } from "../../router/Paths";
import { UserTypeProps } from "../../declarations/userServices";
import { useUser } from "../../hooks/useUser";
import { ErrorSharp } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setToken } from "../../reducer/userReducer";
import { toast } from "react-toastify";
import { useToast } from "../../hooks/useToast";
import ProtocolLocationInput from "../../components/common/ProtocolLocationInput";

function UserTypeSection() {
  const [step, setStep] = useState(1);
  const dispatch = useDispatch();
  const { userType } = useUser();
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { showResponseMessages } = useToast();
  const [formData, setFormData] = useState<UserTypeProps>({
    type: "",
    numberOfEmployees: "",
    numberOfActivities: "",
    locations: [],
    reservation: "",
    legalCompanyName: "",
    website: "",
  });

  const [errors, setErrors] = useState<UserTypeProps | any>({});
  const [selectedLocation, setSelectedLocation] = useState<any>(null);

  const handleNext = () => {
    if (validateFormData()) {
      setStep(2);
    }
  };

  const validateFormData = () => {
    const newErrors: UserTypeProps | any = {};
    if (!formData.type) newErrors.type = true;
    if (!formData.numberOfEmployees) newErrors.numberOfEmployees = true;
    if (!formData.numberOfActivities) newErrors.numberOfActivities = true;
    if (formData.locations.length === 0) newErrors.locations = true;
    if (!formData.reservation) newErrors.reservation = true;

    if (step === 2) {
      if (!formData.legalCompanyName) {
        newErrors.legalCompanyName = "Please enter the legal company name.";
      }
      if (!formData.website) {
        newErrors.website = "Please provide a website URL.";
      } else {
        const domainPattern =
          /^[a-zA-Z0-9][a-zA-Z0-9-]*([.-][a-zA-Z0-9][a-zA-Z0-9-]*)*\.[a-zA-Z]{2,}$/;

        if (!domainPattern.test(formData.website.trim())) {
          newErrors.website = "Please enter a valid website (e.g. example.com)";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFinish = async () => {
    if (!validateFormData()) return;

    try {
      setLoading(true);
      await userType(formData);
      setLoading(false);
    } catch (error) {
      console.error("Error in handleFinish:", error);
      setLoading(false);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target || {};
    if (!name) return; // Skip if no name property (handles ProtocolLocationInput case)

    setFormData((prevData: UserTypeProps | any) => {
      if (name === "locations") {
        const updatedLocations = prevData.locations.includes(value)
          ? prevData.locations.filter((location: []) => location !== value)
          : [...prevData.locations, value];

        return { ...prevData, locations: updatedLocations };
      }
      return { ...prevData, [name]: value };
    });
  };

  const handleEmployeeSelect = (range: string) => {
    setFormData((prevData) => ({ ...prevData, numberOfEmployees: range }));
  };

  const handleActivitieSelect = (range: string) => {
    setFormData((prevData) => ({ ...prevData, numberOfActivities: range }));
  };

  const handleSelectLocation = (location: {
    city?: string;
    country?: string;
    latitude: number;
    longitude: number;
  }) => {
    if (location.city) {
      setFormData((prevData) => ({
        ...prevData,
        locations: [location.city as string],
      }));
      setSelectedLocation(location);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 border border-gray-300 rounded-lg shadow-lg my-10 w-full ">
      {step === 1 && (
        <div>
          <div>
            <div className="flex justify-between">
              <h2 className="text-xl font-bold text-left mb-6">
                Join us as a supply partner
              </h2>
              <p className="text-center mb-4 font-semibold ">Step 1 of 2</p>
            </div>
            <div className="mb-2">
              <div className="flex gap-3 items-center mb-8">
                <p className=" font-semibold ">
                  How do you operate your business?
                </p>
                <p>
                  <PiWarningCircleBold />
                </p>
              </div>
              <div className="flex">
                <button
                  className={`w-full py-2  rounded-tl-md rounded-bl-md border border-gray-300  ${
                    formData.type === "company"
                      ? "bg-blue-600 text-white"
                      : "bg-white"
                  }`}
                  onClick={() => setFormData({ ...formData, type: "company" })}
                >
                  <div
                    className={`flex items-center justify-center gap-4 text-gray-700 ${
                      formData.type === "company"
                        ? "bg-blue-600 text-white"
                        : "bg-white"
                    }`}
                  >
                    <p>
                      <IoBusinessOutline className="text-xl" />
                    </p>
                    <p> As a registered company </p>
                  </div>
                </button>

                <button
                  className={`w-full py-2 rounded-tr-md rounded-br-md border border-gray-300 ${
                    formData.type === "individual"
                      ? "bg-blue-600 text-white"
                      : "bg-white"
                  }`}
                  onClick={() =>
                    setFormData({ ...formData, type: "individual" })
                  }
                >
                  <div
                    className={`flex items-center justify-center gap-2 text-gray-700 ${
                      formData.type === "individual"
                        ? "bg-blue-600 text-white"
                        : "bg-white"
                    }`}
                  >
                    <p>
                      <AiOutlineUser className="text-xl" />
                    </p>
                    <p> As a registered individual </p>
                  </div>
                </button>
              </div>
              <p className={`mt-2 mr-2 ${errors.type ? "text-red-600" : ""}`}>
                {errors.type ? "Please select the type" : ""}
              </p>
            </div>
          </div>
          <div className="my-10">
            <label className="block font-semibold mb-5">
              How many employees does your company have?
            </label>
            <div
              className={`grid grid-cols-5 ${
                errors.numberOfEmployees ? "border border-red-600" : ""
              }`}
            >
              {["Up to 3", "3-10", "11-20", "21-50", "+50"].map((range) => (
                <div
                  key={range}
                  onClick={() => handleEmployeeSelect(range)}
                  className={`py-2 border text-center cursor-pointer ${
                    formData.numberOfEmployees === range
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700"
                  }`}
                >
                  {range}
                </div>
              ))}
            </div>
          </div>
          <div className="mb-10">
            <label className="block font-semibold mb-5">
              How many activities of any type do you offer?
            </label>

            <div
              className={`grid grid-cols-5 ${
                errors.numberOfActivities ? "border border-red-600" : ""
              }`}
            >
              {["Up to 2", "3-6", "7-55", "16-35", "+35"].map((range) => (
                <div
                  key={range}
                  onClick={() => handleActivitieSelect(range)}
                  className={`py-2 border text-center cursor-pointer ${
                    formData.numberOfActivities === range
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700"
                  }`}
                >
                  {range}
                </div>
              ))}
            </div>
          </div>
          <div className="mb-10">
            <label className="block font-semibold mb-5">
              In which location do you provide your activities?
            </label>
            <ProtocolLocationInput
              value={selectedLocation}
              setValue={setSelectedLocation}
              onSelectLocation={handleSelectLocation}
              placeholder="Select a city"
            />
            {errors.locations && (
              <p className="text-red-600 text-sm mt-1">
                Please select a location
              </p>
            )}
          </div>
          <div className="mb-4">
            <label className="block font-semibold mb-5">
              Do you use a reservation system to manage your products?
            </label>
            <div className="flex gap-4">
              <button
                className={`w-full py-2  rounded border border-gray-300 ${
                  formData.reservation === "yes"
                    ? "bg-blue-600 text-white"
                    : "bg-white"
                }`}
                onClick={() => setFormData({ ...formData, reservation: "yes" })}
              >
                Yes
              </button>
              <button
                className={`w-full py-2 rounded border border-gray-300 ${
                  formData.reservation === "no"
                    ? "bg-blue-600 text-white"
                    : "bg-white"
                }`}
                onClick={() => setFormData({ ...formData, reservation: "no" })}
              >
                No
              </button>
            </div>
          </div>
          <div className="mt-9">
            <p>
              Already have account?{" "}
              <span
                className="text-blue-800 cursor-pointer"
                onClick={() => navigate(Paths.login)}
              >
                Sign In
              </span>
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleNext}
              className="w-40 bg-blue-600 text-white py-2 rounded mt-4 "
            >
              Next
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <div className="flex justify-between">
            <h2 className="text-xl font-bold text-left mb-6">
              Join us as a supply partner
            </h2>
            <p className="text-center mb-4 font-semibold ">Step 2 of 2</p>
          </div>
          <div className="mb-8">
            <label className="block font-bold mb-1">Legal company name</label>
            <input
              type="text"
              name="legalCompanyName"
              value={formData.legalCompanyName}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${
                errors.legalCompanyName ? "border-red-600" : "border-gray-300"
              }`}
            />
            {errors.legalCompanyName && (
              <p className="text-red-600">{errors.legalCompanyName}</p>
            )}
          </div>

          <div className="mb-4">
            <label className="block font-bold mb-1">
              Add your company's website link
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${
                errors.website ? "border-red-600" : "border-gray-300"
              }`}
            />
            {errors.website && <p className="text-red-600">{errors.website}</p>}
          </div>

          <div className="my-7">
            <p className="text-gray-600">
              Already have account?{" "}
              <span
                className="text-blue-800 cursor-pointer"
                onClick={() => navigate(Paths.login)}
              >
                Sign In
              </span>
            </p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleFinish}
              className="w-40 bg-blue-600 text-white font-bold py-2 rounded ml-2"
            >
              {" "}
              {loading ? "Please Wait..." : "Finish"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserTypeSection;
